<template>
  <div>
    <topsearch title="Admin / Alan Yonetimi" />
    <div class="flex">
      <div class="w-72 border-r h-full" style="min-width: 217px">
        <adminSidebar />
      </div>
      <div class="w-full md:p-4">
        <div class="mb-4 flex items-center justify-between">
          <div class="flex items-center space-x-4">
            <h2 class="text-lg font-semibold text-gray-800">Form Alanlari Yonetimi</h2>
            <select v-model="selectedTable" @change="loadFields" class="uk-select" style="width: 200px">
              <option value="">Tablo Secin</option>
              <option value="leads">Leads</option>
              <option value="customers">Customers</option>
              <option value="users">Users</option>
            </select>
          </div>
          <div class="flex items-center space-x-2">
            <button
              v-if="hasChanges"
              @click="saveChanges"
              :disabled="saving"
              class="bg-tile-500 text-white px-4 py-2 rounded-lg hover:bg-tile-600 cursor-pointer"
            >
              <span v-if="saving">Kaydediliyor...</span>
              <span v-else>Degisiklikleri Kaydet</span>
            </button>
            <span v-if="hasChanges" class="text-sm text-orange-600">
              Kaydedilmemis degisiklikler var
            </span>
          </div>
        </div>

        <div v-if="loading" class="text-center py-8 text-gray-500">
          Yukleniyor...
        </div>

        <div v-else-if="selectedTable && categories.length > 0" class="space-y-4">
          <div class="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-800 mb-4">
            <strong>Bilgi:</strong> Alanlari surukleyerek siralayabilir veya farkli kategorilere tasiyabilirsiniz.
            Yeni kategori eklemek icin alttaki formu kullanin.
          </div>

          <!-- Category sections with draggable -->
          <div
            v-for="category in categories"
            :key="category"
            class="bg-white border rounded-lg overflow-hidden"
          >
            <div class="bg-gray-100 px-4 py-3 flex items-center justify-between">
              <h3 class="font-medium text-gray-700">{{ category || 'Kategori Yok' }}</h3>
              <span class="text-xs text-gray-500">{{ getCategoryFieldCount(category) }} alan</span>
            </div>

            <draggable
              v-model="fieldsByCategory[category]"
              :group="{ name: 'fields', pull: true, put: true }"
              draggable=".field-item"
              class="min-h-[50px] p-2"
              ghost-class="ghost-field"
              @change="onDragChange($event, category)"
            >
              <div
                v-for="field in fieldsByCategory[category]"
                :key="field.id"
                class="field-item flex items-center space-x-3 p-3 border rounded mb-2 bg-white hover:bg-gray-50 cursor-move"
                :class="{ 'opacity-50': field.status != 1 }"
              >
                <div class="text-gray-400">
                  <vk-icon icon="list"></vk-icon>
                </div>
                <div class="flex-1">
                  <div class="font-medium text-gray-800">{{ field.label || field.name }}</div>
                  <div class="text-xs text-gray-500">
                    <span class="mr-2">Alan: {{ field.name }}</span>
                    <span class="mr-2">|</span>
                    <span>Tip: {{ field.component_name || 'Bilinmiyor' }}</span>
                    <span v-if="field.status != 1" class="ml-2 text-red-500">(Pasif)</span>
                  </div>
                </div>
                <div class="text-xs text-gray-400">
                  #{{ field.sort_order || '-' }}
                </div>
              </div>
            </draggable>
          </div>

          <!-- Add new category -->
          <div class="border-2 border-dashed border-gray-300 rounded-lg p-4">
            <div class="flex items-center space-x-3">
              <input
                v-model="newCategory"
                type="text"
                class="uk-input flex-1"
                placeholder="Yeni kategori adi..."
                @keyup.enter="addCategory"
              />
              <button
                @click="addCategory"
                :disabled="!newCategory.trim()"
                class="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 disabled:opacity-50 cursor-pointer"
              >
                + Kategori Ekle
              </button>
            </div>
          </div>
        </div>

        <div v-else-if="selectedTable && !loading" class="text-center py-8 text-gray-500">
          Bu tablo icin alan bulunamadi.
        </div>

        <div v-else class="text-center py-8 text-gray-500">
          Yukaridan bir tablo secin.
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SidebarVue from "../components/Ui/Admin/Sidebar.vue";
import httpService from "../services/http";
import draggable from "vuedraggable";

export default {
  name: "AdminFieldManager",
  components: {
    adminSidebar: SidebarVue,
    draggable,
  },
  data() {
    return {
      selectedTable: "",
      fieldsByCategory: {},
      categories: [],
      loading: false,
      saving: false,
      hasChanges: false,
      newCategory: "",
      originalFieldsState: "",
    };
  },
  methods: {
    getCategoryFieldCount(category) {
      return this.fieldsByCategory[category] ? this.fieldsByCategory[category].length : 0;
    },

    async loadFields() {
      if (!this.selectedTable) {
        this.fieldsByCategory = {};
        this.categories = [];
        return;
      }

      this.loading = true;
      this.hasChanges = false;

      try {
        const response = await httpService.getFieldsForAdmin(this.selectedTable);

        if (response.data.success) {
          const fields = response.data.fields.map((f, index) => ({
            ...f,
            sort_order: f.sort_order !== null ? f.sort_order : index * 10,
            category: f.category || 'Diger'
          }));

          // Group fields by category
          const grouped = {};
          const categoryOrder = [];

          fields.forEach(f => {
            if (!grouped[f.category]) {
              grouped[f.category] = [];
              categoryOrder.push(f.category);
            }
            grouped[f.category].push(f);
          });

          this.fieldsByCategory = grouped;
          this.categories = categoryOrder;

          // Store original state for comparison
          this.originalFieldsState = this.getFieldsStateString();
        } else {
          this.$root.errorMessage(response.data.message || "Alanlar yuklenemedi");
        }
      } catch (error) {
        console.error("Load fields error:", error);
        this.$root.errorMessage("Alanlar yuklenirken hata olustu");
      } finally {
        this.loading = false;
      }
    },

    getFieldsStateString() {
      const allFields = [];
      this.categories.forEach(cat => {
        if (this.fieldsByCategory[cat]) {
          this.fieldsByCategory[cat].forEach(f => {
            allFields.push({
              id: f.id,
              sort_order: f.sort_order,
              category: f.category
            });
          });
        }
      });
      return JSON.stringify(allFields);
    },

    getAllFields() {
      const allFields = [];
      this.categories.forEach(cat => {
        if (this.fieldsByCategory[cat]) {
          this.fieldsByCategory[cat].forEach(f => {
            allFields.push(f);
          });
        }
      });
      return allFields;
    },

    onDragChange(evt, category) {
      // Update category for moved items
      if (evt.added) {
        evt.added.element.category = category;
      }

      // Recalculate sort_order for all fields
      this.recalculateSortOrder();
      this.checkForChanges();
    },

    recalculateSortOrder() {
      let globalOrder = 0;

      this.categories.forEach(category => {
        if (this.fieldsByCategory[category]) {
          this.fieldsByCategory[category].forEach((field) => {
            field.sort_order = globalOrder * 10;
            globalOrder++;
          });
        }
      });
    },

    checkForChanges() {
      const currentState = this.getFieldsStateString();
      this.hasChanges = currentState !== this.originalFieldsState;
    },

    addCategory() {
      const categoryName = this.newCategory.trim();
      if (!categoryName) return;

      if (this.categories.includes(categoryName)) {
        this.$root.errorMessage("Bu kategori zaten mevcut");
        return;
      }

      this.categories.push(categoryName);
      this.$set(this.fieldsByCategory, categoryName, []);
      this.newCategory = "";
    },

    async saveChanges() {
      this.saving = true;

      try {
        const allFields = this.getAllFields();
        const updates = allFields.map(f => ({
          id: f.id,
          sort_order: f.sort_order,
          category: f.category
        }));

        const response = await httpService.updateFieldOrder(updates);

        if (response.data.success) {
          this.$root.successMessage(`${response.data.updated} alan guncellendi`);
          this.originalFieldsState = this.getFieldsStateString();
          this.hasChanges = false;
        } else {
          this.$root.errorMessage(response.data.message || "Kayit basarisiz");
        }
      } catch (error) {
        console.error("Save error:", error);
        this.$root.errorMessage("Kayit sirasinda hata olustu");
      } finally {
        this.saving = false;
      }
    },
  },
  created() {
    // Check if user is admin
    const cache = this.$root.cache || {};
    const me = cache.me || {};
    const allowedDepartments = ['admin', 'admin1', 'admin2'];
    if (!allowedDepartments.includes(me.department)) {
      this.$router.push('/');
      return;
    }
  },
};
</script>

<style scoped>
.ghost-field {
  opacity: 0.5;
  background: #c8ebfb;
}
</style>
